import { useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Field, TextInput } from "@/components/admin/Field";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export function RulesSync({ initialUrl, lastSyncAt, autoSync, lastAutoSync, blockedSync, onSynced }) {
  const [url, setUrl] = useState(initialUrl || "");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [forceConfirm, setForceConfirm] = useState(false);
  const [auto, setAuto] = useState(autoSync !== false);

  const toggleAuto = async (v) => {
    setAuto(v);
    try {
      await api.put("/admin/rules/sync-settings", { auto_sync: v });
      toast.success(v ? "Sincronização automática diária ativada (06:00)." : "Sincronização automática desativada.");
    } catch (e) {
      setAuto(!v);
      toast.error(errorMessage(e));
    }
  };

  const run = async (confirmed, force = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/rules/sync", { doc_url: url, confirm: confirmed, force });
      if (data.preview) {
        setPreview(data);
      } else if (data.blocked) {
        setPreview(null);
        toast.error("Importação bloqueada: o documento reduziria demasiado as regras. Vê o aviso.");
        onSynced();
      } else {
        setPreview(null);
        toast.success(`Importadas ${data.rules_imported} regras em ${data.categories_imported} categorias.`);
        onSynced();
      }
    } catch (e) {
      toast.error(errorMessage(e, "Não foi possível sincronizar."));
    } finally {
      setBusy(false);
      setConfirm(false);
      setForceConfirm(false);
    }
  };

  const dropText = (b) => `${b.categories.current} → ${b.categories.incoming} categorias · ${b.rules.current} → ${b.rules.incoming} regras`;

  return (
    <section className="card-eden p-5" data-testid="rules-sync-panel">
      {blockedSync && (
        <div className="mb-5 rounded-md border border-red-500/50 bg-red-500/10 p-4" role="alert" data-testid="rules-sync-blocked">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-400" aria-hidden="true" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-300">Sincronização bloqueada por segurança</p>
              <p className="mt-1 text-sm text-eden-secondary">O documento reduziria drasticamente o regulamento: <strong className="text-eden-text" data-testid="rules-sync-blocked-numbers">{dropText(blockedSync)}</strong>. Nada foi alterado no site. Confirma que o Google Doc está completo (títulos “1 - …”, “2 - …”) antes de aplicar.</p>
              <p className="mt-1 text-xs text-eden-muted">Detetado em {formatDate(blockedSync.at)} · origem: {blockedSync.source}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" className="btn-secondary h-9 text-xs" disabled={busy || url.length < 10} onClick={() => run(false)} data-testid="rules-sync-blocked-preview">Rever documento</button>
                <button type="button" className="h-9 rounded-md border border-red-500/60 px-3 text-xs font-medium text-red-300 transition-colors hover:bg-red-500/20" disabled={busy || url.length < 10} onClick={() => setForceConfirm(true)} data-testid="rules-sync-force-button">Aplicar mesmo assim</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-base font-semibold text-eden-text">Sincronizar com o Google Doc</h3>
          <p className="mt-1 text-sm text-eden-muted">Lê o documento e substitui todas as regras pelo seu conteúdo. O documento tem de estar partilhado como “Qualquer pessoa com a ligação”.{lastSyncAt ? ` Última sincronização: ${formatDate(lastSyncAt)}.` : ""}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1"><Field label="Ligação do documento" id="rules-doc-url"><TextInput id="rules-doc-url" value={url} onChange={setUrl} placeholder="https://docs.google.com/document/d/…" /></Field></div>
        <button type="button" className="btn-secondary h-10" disabled={busy || url.length < 10} onClick={() => run(false)} data-testid="rules-sync-preview-button"><RefreshCw size={14} className={busy ? "animate-spin" : ""} /> {busy ? "A ler…" : "Pré-visualizar importação"}</button>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-eden-border bg-eden-bg px-4 py-3" data-testid="rules-auto-sync">
        <label className="flex items-center gap-3 text-sm text-eden-text">
          <Switch checked={auto} onCheckedChange={toggleAuto} data-testid="rules-auto-sync-switch" aria-label="Sincronização automática diária" />
          Sincronização automática todos os dias às 06:00 (Lisboa)
        </label>
        <p className="text-xs text-eden-muted" data-testid="rules-auto-sync-status">
          {lastAutoSync
            ? `Última execução automática: ${formatDate(lastAutoSync.finished_at)}: ${lastAutoSync.status === "ok" ? "concluída" : lastAutoSync.status === "skipped" ? "ignorada" : "falhou"}${lastAutoSync.detail ? ` (${lastAutoSync.detail})` : ""}`
            : "Ainda não houve execuções automáticas."}
        </p>
      </div>
      {preview && (
        <div className="mt-5 rounded-md border border-eden-border bg-eden-bg p-4" data-testid="rules-sync-preview">
          <p className="text-sm text-eden-text">O documento contém <strong>{preview.total} regras</strong> em <strong>{preview.categories.length} categorias</strong> (atualmente: {preview.current.rules} regras em {preview.current.categories} categorias).</p>
          {preview.warning && (
            <p className="mt-2 flex items-start gap-2 rounded-md border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-300" data-testid="rules-sync-preview-warning">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>Atenção: esta importação reduz o regulamento em mais de 30% ({dropText(preview.warning)}). Será bloqueada a menos que forces a aplicação.</span>
            </p>
          )}
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {preview.categories.map((c, i) => (
              <li key={c.title} className="text-sm text-eden-secondary"><span className="font-mono text-xs text-eden-orange">{String(i + 1).padStart(2, "0")}</span> {c.title} <span className="text-eden-muted">· {c.count}</span>{c.groups.length > 0 && <span className="block pl-6 text-xs text-eden-muted">{c.groups.join(" · ")}</span>}</li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className="btn-primary h-10" disabled={busy} onClick={() => (preview.warning ? setForceConfirm(true) : setConfirm(true))} data-testid="rules-sync-import-button">{preview.warning ? "Aplicar mesmo assim" : "Importar e substituir"}</button>
            <button type="button" className="btn-ghost h-10" onClick={() => setPreview(null)} data-testid="rules-sync-cancel-button">Cancelar</button>
          </div>
        </div>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="border-eden-border bg-eden-surface" data-testid="rules-sync-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-eden-text">Substituir todas as regras?</AlertDialogTitle>
            <AlertDialogDescription className="text-eden-secondary">As regras e categorias atuais serão apagadas e substituídas pelo conteúdo do documento. Edições feitas aqui no painel que não estejam no documento serão perdidas. Esta ação é imediata no site público.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-eden-border bg-transparent text-eden-text hover:bg-eden-elevated" data-testid="rules-sync-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => run(true)} className="bg-eden-orange text-eden-bg hover:bg-eden-orangeHover" data-testid="rules-sync-confirm-import">Sim, importar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={forceConfirm} onOpenChange={setForceConfirm}>
        <AlertDialogContent className="border-red-500/50 bg-eden-surface" data-testid="rules-sync-force-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-red-300">Aplicar uma redução drástica?</AlertDialogTitle>
            <AlertDialogDescription className="text-eden-secondary">O documento tem muito menos categorias ou regras do que o site atual. Se o Google Doc estiver incompleto ou com títulos partidos, o regulamento público fica danificado. As categorias em falta ficam escondidas (não apagadas) e voltam quando reaparecerem no documento.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-eden-border bg-transparent text-eden-text hover:bg-eden-elevated" data-testid="rules-sync-force-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => run(true, true)} className="bg-red-500 text-white hover:bg-red-600" data-testid="rules-sync-force-confirm">Sim, aplicar mesmo assim</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
